import { dlog } from '../utils/debug';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, Bell, Info, Warning, Target, Coffee, Palette } from 'phosphor-react';
import { ref, set, get } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getContrastTextColor } from '../utils/contrast';
import FEATURES, { isFeatureEnabled } from '../config/features';
import useSubscription from '../hooks/useSubscription';
import { SUPPORT_URL, SUPPORT_HANDLE } from '../config/support';

// Use mock FCM in development (localhost/local IP), real FCM in production
const hostname = window.location.hostname;
const isDev = hostname === 'localhost' || 
              hostname === '127.0.0.1' || 
              hostname.startsWith('192.168.') || 
              hostname.startsWith('10.0.') ||
              window.location.protocol === 'http:';

const fcmModule = isDev 
  ? require('../utils/fcm-mock')
  : require('../utils/fcm');

const {
  requestNotificationPermission,
  hasNotificationPermission,
  deleteNotificationToken,
  sendTestNotification
} = fcmModule;
import '../styles/design-system.css';
import '../styles/SettingsPage.css';

function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { isPro, subscriptionData } = useSubscription();
  const [defaultTeamName, setDefaultTeamName] = useState('Team A');
  const [defaultTeamColor, setDefaultTeamColor] = useState('#AE1F24');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [standardNotifications, setStandardNotifications] = useState(false);
  const [advancedNotifications, setAdvancedNotifications] = useState(false);
  const [showAdvancedWarning, setShowAdvancedWarning] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default'); // 'default' | 'granted' | 'denied'
  const [showApiError, setShowApiError] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [favoriteTeamDetected, setFavoriteTeamDetected] = useState(null);
  const [autoDetectFavoriteTeam, setAutoDetectFavoriteTeam] = useState(true);

  const teamColorContrast = getContrastTextColor(defaultTeamColor);

  // Load settings from Firebase
  useEffect(() => {
    if (!user) {
      setSettingsLoading(false);
      return;
    }

    const loadUserSettings = async () => {
      try {
        const settingsRef = ref(db, `users/${user.uid}/settings`);
        const snapshot = await get(settingsRef);
        
        if (snapshot.exists()) {
          const settings = snapshot.val();
          if (settings.defaultTeamName) setDefaultTeamName(settings.defaultTeamName);
          if (settings.defaultTeamColor) setDefaultTeamColor(settings.defaultTeamColor);
          if (settings.notificationsEnabled !== undefined) {
            setNotificationsEnabled(settings.notificationsEnabled);
          }
          if (settings.standardNotifications !== undefined) {
            setStandardNotifications(settings.standardNotifications);
          }
          if (settings.advancedNotifications !== undefined) {
            setAdvancedNotifications(settings.advancedNotifications);
          }
          if (settings.autoDetectFavoriteTeam !== undefined) {
            setAutoDetectFavoriteTeam(settings.autoDetectFavoriteTeam);
          }
          
          // Check for favorite team detection
          if (settings.favoriteTeamDetected && settings.favoriteTeamDetectedAt) {
            setFavoriteTeamDetected({
              name: settings.defaultTeamName,
              color: settings.defaultTeamColor,
              usageCount: settings.favoriteTeamUsageCount || 0,
              detectedAt: settings.favoriteTeamDetectedAt
            });
          }
        }
        
        // Check browser permission status
        const hasPermission = hasNotificationPermission();
        const browserPermission = 'Notification' in window ? Notification.permission : 'default';
        setPermissionStatus(browserPermission);
        
        if (hasPermission && !notificationsEnabled) {
          // If permission granted but toggle off, sync state
          setNotificationsEnabled(true);
          saveSetting('notificationsEnabled', true);
        } else if (browserPermission === 'denied' && notificationsEnabled) {
          // If permission blocked but toggle on, turn it off
          setNotificationsEnabled(false);
          saveSetting('notificationsEnabled', false);
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadUserSettings();
  }, [user]);

  // Listen for favorite team detection events
  useEffect(() => {
    const handleFavoriteTeamDetected = (event) => {
      dlog('🎯 Favorite team detected in settings:', event.detail);
      setFavoriteTeamDetected(event.detail);
    };

    window.addEventListener('favoriteTeamDetected', handleFavoriteTeamDetected);
    
    return () => {
      window.removeEventListener('favoriteTeamDetected', handleFavoriteTeamDetected);
    };
  }, []);

  // Save settings to Firebase
  const saveSetting = async (settingKey, value) => {
    if (!user) return;
    
    try {
      const settingsRef = ref(db, `users/${user.uid}/settings/${settingKey}`);
      await set(settingsRef, value);
    } catch (error) {
      console.error('Error saving setting:', error);
    }
  };

  const handleTeamNameChange = (e) => {
    const value = e.target.value;
    setDefaultTeamName(value);
    saveSetting('defaultTeamName', value);
  };

  const handleTeamColorChange = (e) => {
    const value = e.target.value;
    setDefaultTeamColor(value);
    saveSetting('defaultTeamColor', value);
  };

  const handleAutoDetectToggle = (e) => {
    const value = e.target.checked;
    setAutoDetectFavoriteTeam(value);
    saveSetting('autoDetectFavoriteTeam', value);
  };

  const handleNotificationsToggle = async (e) => {
    const value = e.target.checked;
    
    if (value) {
      // Optimistically enable toggle (better UX)
      setNotificationsEnabled(true);
      
      // Check current permission state
      const currentPermission = Notification.permission;
      
      if (currentPermission === 'denied') {
        // Permission was previously denied - show helpful instructions
        toast.warning(
          'Notifications are blocked in your browser.\n' +
          'Click the lock icon in your address bar, change Notifications to Allow, then refresh.',
          10000
        );
        setNotificationsEnabled(false);
        return;
      }
      
      // Request permission (async, don't block UI)
      try {
        const token = await requestNotificationPermission(user);
        if (token) {
          // Success - already enabled optimistically
          saveSetting('notificationsEnabled', true);
          dlog('✅ Notifications enabled successfully');
        } else {
          // Failed - revert toggle and show API error banner
          setNotificationsEnabled(false);
          saveSetting('notificationsEnabled', false);
          setShowApiError(true); // Show banner when token request fails
          dlog('⚠️ Notification setup failed - showing API error banner');
        }
      } catch (error) {
        // Error occurred - revert toggle
        console.error('Notification error:', error);
        setNotificationsEnabled(false);
        saveSetting('notificationsEnabled', false);
        setShowApiError(true); // Show banner on any error
      }
    } else {
      // Disable notifications
      setNotificationsEnabled(false);
      setStandardNotifications(false);
      setAdvancedNotifications(false);
      saveSetting('notificationsEnabled', false);
      saveSetting('standardNotifications', false);
      saveSetting('advancedNotifications', false);
      
      // Delete FCM tokens
      await deleteNotificationToken(user);
    }
  };
  
  const handleStandardToggle = (e) => {
    const value = e.target.checked;
    setStandardNotifications(value);
    saveSetting('standardNotifications', value);
  };
  
  const handleAdvancedToggle = (e) => {
    const value = e.target.checked;
    
    if (value) {
      // Show warning before enabling
      setShowAdvancedWarning(true);
    } else {
      setAdvancedNotifications(false);
      saveSetting('advancedNotifications', false);
    }
  };
  
  const confirmAdvancedNotifications = () => {
    setAdvancedNotifications(true);
    setShowAdvancedWarning(false);
    saveSetting('advancedNotifications', true);
  };
  
  const cancelAdvancedNotifications = () => {
    setShowAdvancedWarning(false);
  };

  const handleBackClick = () => {
    navigate('/', { state: { openDrawer: true } });
  };

  if (settingsLoading) {
    return (
      <div className="settings-page">
        <div className="page-header">
          <button className="back-button" onClick={handleBackClick}>
            <CaretLeft size={24} weight="bold" />
          </button>
          <h1 className="page-title">Settings</h1>
          <div className="header-spacer"></div>
        </div>
        <div className="page-content">
          <div className="content-section">
            <p className="body-text">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <button className="back-button" onClick={handleBackClick}>
          <CaretLeft size={24} weight="bold" />
        </button>
        <h1 className="page-title">Settings</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="page-content">
        <div className="content-section">
          <h2 className="section-title">
            Favorite Team
          </h2>
          
          <div className="setting-item team-setting">
            {/* Subtle favorite team detection indicator - above inputs */}
            <div className="team-name-field">
              <label
                className="team-color-chip"
                htmlFor="team-name-input"
                style={{ backgroundColor: defaultTeamColor }}
              >
                <input
                  type="color"
                  value={defaultTeamColor}
                  onChange={handleTeamColorChange}
                  className="team-color-input"
                  aria-label="Team color"
                />
                <Palette
                  size={14}
                  weight="fill"
                  className="team-color-icon"
                  style={{ color: teamColorContrast }}
                />
              </label>
              <input
                type="text"
                value={defaultTeamName}
                onChange={handleTeamNameChange}
                className="setting-input team-name-input"
                id="team-name-input"
                placeholder="Enter team name"
              />
            </div>
          </div>
          
          {favoriteTeamDetected && (
            <div className="detection-indicator subtle">
              <Target size={14} weight="fill" className="detection-icon-small" />
              <span className="detection-text">Auto-detected · {favoriteTeamDetected.usageCount} recent matches</span>
            </div>
          )}
          
          {/* Auto-detect favorite team toggle */}
          <div className="setting-item">
            <label htmlFor="auto-detect-favorite-team" className="toggle-container">
              <span className="toggle-text">
                <span className="toggle-label-text">Auto-detect</span>
                <span className="toggle-subtext">Keeps this team in sync with your latest matches.</span>
              </span>
              <input
                type="checkbox"
                id="auto-detect-favorite-team"
                checked={autoDetectFavoriteTeam}
                onChange={handleAutoDetectToggle}
                className="toggle-input"
              />
              <span className="toggle-label">
                <span className="toggle-slider"></span>
              </span>
            </label>
          </div>
        </div>

        <div className="content-section support-settings-section">
          <h2 className="section-title">Support What's The Score?</h2>
          <div className="support-settings-card">
            <p>
              What’s The Score? exists because gym scoreboards are hard to see and parents keep asking, “What’s the score?” Now you can stay put and still follow the game.
            </p>
            <p>
              If it makes your day easier, you can buy me a coffee or reach me anytime at <span className="support-handle">{SUPPORT_HANDLE}</span>.
            </p>
            <a
              className="support-button support-settings-button"
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Coffee size={16} weight="fill" />
              Buy me a coffee
            </a>
          </div>
        </div>

        {/* Stripe subscription debug tools removed — payments are
            dormant by design (PAYMENTS: false). When/if Stripe is
            reactivated, restore these from git history. */}

        {/* Notifications Section - Controlled by Feature Flag */}
        {FEATURES.NOTIFICATIONS && (
        <div className="content-section">
          <h2 className="section-title">
            Notifications
          </h2>
          
          {/* Firebase API Error Warning */}
          {showApiError && (
            <div className="permission-blocked-banner api-error-banner">
              <div className="blocked-header">
                <Info size={20} weight="fill" style={{ color: '#FF9800' }} />
                <strong>Firebase Setup In Progress</strong>
              </div>
              <p className="body-text">
                The Firebase Installations API is enabled, but it may take 5-10 minutes to propagate.
              </p>
              <p className="body-text">
                <strong>Meanwhile, you can also upgrade your Firebase plan:</strong>
              </p>
              <p className="body-text">
                Notifications require the <strong>Blaze plan (pay-as-you-go)</strong> for Cloud Functions.
              </p>
              <p className="body-text how-to-enable">
                <strong>To upgrade:</strong>
              </p>
              <ol className="body-text enable-steps">
                <li>Go to: <a href="https://console.firebase.google.com/usage/details" target="_blank" rel="noopener noreferrer" style={{color: '#FF9800', textDecoration: 'underline'}}>Firebase Billing</a></li>
                <li>Click "Upgrade to Blaze"</li>
                <li>Add payment method (free tier is generous!)</li>
                <li>Wait 5-10 minutes, then try notifications again</li>
              </ol>
              <button 
                onClick={() => setShowApiError(false)}
                style={{
                  marginTop: '1rem',
                  padding: '0.8rem 1.2rem',
                  background: '#FF9800',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.6rem',
                  cursor: 'pointer',
                  fontFamily: 'Nunito Sans',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600
                }}
              >
                Got it, waiting for setup to complete
              </button>
            </div>
          )}

          {/* Blocked Permission Warning */}
          {permissionStatus === 'denied' && (
            <div className="permission-blocked-banner">
              <div className="blocked-header">
                <Warning size={20} weight="fill" style={{ color: '#D32F2F' }} />
                <strong>Notifications Blocked</strong>
              </div>
              <p className="body-text">
                You previously blocked notifications for this site.
              </p>
              <p className="body-text how-to-enable">
                <strong>To enable:</strong>
              </p>
              <ol className="body-text enable-steps">
                <li>Click the 🔒 or ⓘ icon in your address bar</li>
                <li>Find "Notifications" and change to "Allow"</li>
                <li>Refresh this page and toggle on</li>
              </ol>
            </div>
          )}
          
          {/* Master Notification Toggle */}
          <div className="setting-item">
            <label htmlFor="notifications-enabled" className="toggle-container">
              <span className="toggle-label-text">Enable Notifications</span>
              <input
                type="checkbox"
                id="notifications-enabled"
                checked={notificationsEnabled}
                onChange={handleNotificationsToggle}
                className="toggle-input"
                disabled={permissionStatus === 'denied'}
              />
              <span className={`toggle-label ${permissionStatus === 'denied' ? 'disabled' : ''}`}>
                <span className="toggle-slider"></span>
              </span>
            </label>
            <p className="setting-hint">
              {permissionStatus === 'denied' 
                ? 'Blocked in browser settings' 
                : 'Required for all notification types'}
            </p>
          </div>

          {/* Essential Tier (Always On) */}
          {notificationsEnabled && (
            <div className="notification-tier">
              <h3 className="subsection-title">Essential Notifications</h3>
              <p className="body-text notification-description">Always enabled</p>
              <ul className="notification-list body-text">
                <li>Match ends</li>
                <li>Match invites</li>
              </ul>
            </div>
          )}

          {/* Standard Tier (Opt-In) */}
          {notificationsEnabled && (
            <div className="notification-tier">
              <div className="setting-item">
                <label htmlFor="standard-notifications" className="toggle-container">
                  <span className="toggle-label-text">Standard Notifications</span>
                  <input
                    type="checkbox"
                    id="standard-notifications"
                    checked={standardNotifications}
                    onChange={handleStandardToggle}
                    className="toggle-input"
                  />
                  <span className="toggle-label">
                    <span className="toggle-slider"></span>
                  </span>
                </label>
                <p className="setting-hint">Recommended</p>
              </div>
              <ul className="notification-list body-text">
                <li>Set completions</li>
                <li>Close moments (24-24)</li>
                <li>Your matches starting</li>
              </ul>
            </div>
          )}

          {/* Advanced Tier (High Frequency Warning) */}
          {notificationsEnabled && (
            <div className="notification-tier advanced-tier">
              <div className="setting-item">
                <label htmlFor="advanced-notifications" className="toggle-container">
                  <span className="toggle-label-text">
                    <Warning size={16} weight="fill" style={{ marginRight: '0.4rem', color: '#D32F2F' }} />
                    Advanced Notifications
                  </span>
                  <input
                    type="checkbox"
                    id="advanced-notifications"
                    checked={advancedNotifications}
                    onChange={handleAdvancedToggle}
                    className="toggle-input"
                  />
                  <span className="toggle-label">
                    <span className="toggle-slider"></span>
                  </span>
                </label>
                <p className="setting-hint warning-text">High frequency - Use carefully</p>
              </div>
              <ul className="notification-list body-text">
                <li>Every score update</li>
                <li>Momentum shifts</li>
                <li>Timeouts & substitutions</li>
              </ul>
              <div className="advanced-warning">
                <p className="body-text">
                  ⚠️ You may receive 50+ notifications per match. 
                  Only enable for matches you're actively following.
                </p>
              </div>
            </div>
          )}

          {/* Test Notification Button */}
          {notificationsEnabled && (
            <div className="setting-item">
              <button 
                className="test-notification-btn"
                onClick={sendTestNotification}
              >
                Send Test Notification
              </button>
            </div>
          )}
        </div>
        )} {/* END NOTIFICATIONS FEATURE FLAG */}

        {/* Advanced Notification Warning Modal - Outside feature flag */}
        {FEATURES.NOTIFICATIONS && showAdvancedWarning && (
          <div className="modal-overlay" onClick={cancelAdvancedNotifications}>
            <div className="modal-content advanced-warning-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <Warning size={32} weight="fill" style={{ color: '#D32F2F' }} />
                <h3 className="subsection-title">Enable Advanced Notifications?</h3>
              </div>
              <div className="modal-body">
                <p className="body-text">
                  Advanced notifications include <strong>every score update</strong> during a match.
                </p>
                <p className="body-text">
                  You may receive <strong>50+ notifications per match</strong>.
                </p>
                <p className="body-text">
                  This is recommended <strong>only when you can't attend</strong> a match in person 
                  and want real-time play-by-play updates.
                </p>
              </div>
              <div className="modal-actions">
                <button className="modal-btn cancel-btn" onClick={cancelAdvancedNotifications}>
                  Cancel
                </button>
                <button className="modal-btn confirm-btn" onClick={confirmAdvancedNotifications}>
                  Enable Advanced
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="content-section">
          <div className="setting-item">
            <div className="about-info">
              <p className="app-name">What's The Score?</p>
              <p className="app-version">Version 1.0.0</p>
              <p className="app-description">
                Real time volleyball tracking and sharing by Nic, just another parent that can't see the score.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
